/**
 * Request schemas for the auth routes. Shared so the SPA validates with exactly
 * the same rules the API enforces — a client-side check that disagrees with the
 * server is worse than no check at all.
 */
import { z } from 'zod';
import { PLANS } from '../plan/gate.js';
import { JOB_TITLES, ROLES, isWeakPin } from './roles.js';
export const BILLING_CYCLES = ['monthly', 'yearly'];
/**
 * Emails are stored lower-cased and trimmed, and `users_email_idx` is unique on
 * the raw column — so normalising here is what makes that constraint mean
 * "one person, one login" rather than letting Alice@ and alice@ both register.
 */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(254)
    .email('Enter a valid email address');
/**
 * Length over composition rules, per NIST SP 800-63B: forced symbols and digits
 * push people towards `Password1!`, which is weaker than a long passphrase. The
 * 72-byte ceiling is bcrypt's; argon2id has no such limit, but capping input
 * bounds the work an unauthenticated request can force us to do.
 */
export const passwordSchema = z
    .string()
    .min(10, 'Use at least 10 characters')
    .max(200, 'Use no more than 200 characters');
/** 4–6 digits, for the shared-device quick-switch at the counter. */
export const pinSchema = z
    .string()
    .regex(/^\d{4,6}$/, 'PIN must be 4 to 6 digits');
export const signupSchema = z.object({
    clinicName: z.string().trim().min(1, 'Enter your clinic name').max(120),
    ownerName: z.string().trim().min(1, 'Enter your name').max(120),
    email: emailSchema,
    password: passwordSchema,
    plan: z.enum(PLANS),
    cycle: z.enum(BILLING_CYCLES),
});
export const loginSchema = z.object({
    email: emailSchema,
    // Not `passwordSchema`: an existing password set under older rules must still
    // be able to log in. Validating length here would lock those users out.
    password: z.string().min(1, 'Enter your password'),
});
export const forgotPasswordSchema = z.object({ email: emailSchema });
/** Opaque 32-byte base64url token from an emailed link. */
export const tokenOnlySchema = z.object({
    token: z.string().min(20, 'Missing or malformed token').max(200),
});
/** A new password must meet current rules, unlike one being used to log in. */
export const resetPasswordSchema = tokenOnlySchema.extend({
    password: passwordSchema,
});
/**
 * Changing your own password while signed in.
 *
 * `currentPassword` is `min(1)` rather than `passwordSchema` for the same reason
 * the login schema is: it is an existing credential, possibly set under older
 * rules, and validating it against today's requirements would refuse a correct
 * password before it was ever checked. The *new* one must meet current rules.
 */
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    password: passwordSchema,
});
// ------------------------------------------------------------------- team
/**
 * Inviting someone. `role` is the permission tier the API enforces; `jobTitle`
 * is descriptive. Both are supplied because a trusted injector may well be a
 * manager — see ./roles.ts on why they are separate.
 */
export const inviteSchema = z.object({
    name: z.string().trim().min(1, 'Enter their name').max(120),
    email: emailSchema,
    role: z.enum(ROLES),
    jobTitle: z.enum(JOB_TITLES),
});
/**
 * Activating an invitation. `name` is optional and overrides what the inviter
 * typed — people know how to spell their own name better than their manager does.
 */
export const activateSchema = tokenOnlySchema.extend({
    password: passwordSchema,
    name: z.string().trim().min(1).max(120).optional(),
});
export const changeRoleSchema = z.object({
    role: z.enum(ROLES),
    jobTitle: z.enum(JOB_TITLES),
});
// ------------------------------------------------------------- PIN switching
/**
 * Setting your own PIN. Weak PINs are refused here AND server-side — a 4-digit
 * secret with 5 attempts before lockout only works if the obvious guesses are
 * unavailable.
 */
export const setPinSchema = z.object({
    pin: pinSchema.refine((p) => !isWeakPin(p), {
        message: 'Avoid repeated digits or a simple run like 1234',
    }),
});
/** Switching to a colleague on a shared device. */
export const pinSwitchSchema = z.object({
    userId: z.string().uuid('Pick who you are'),
    pin: pinSchema,
});
//# sourceMappingURL=schemas.js.map